import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { theme } from '../constants/theme';

const StyledButton = styled(Button)(() => ({
  backgroundColor: theme.colors.primary,
  color: '#fff',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 24px',
  '&:hover': {
    backgroundColor: '#c73e24',
  },
}));

const StyledTextarea = styled('textarea')(() => ({
  width: '100%',
  fontFamily: 'monospace',
  fontSize: '12px',
  padding: '12px',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: `${theme.borderRadius.md}px`,
  backgroundColor: theme.colors.surface,
  resize: 'vertical',
  minHeight: '300px',
  '&:focus': {
    outline: 'none',
    borderColor: theme.colors.primary,
    borderWidth: '2px',
  }
}));

const LinkCoderDialog = ({ isOpen, onClose, selectedItems, linkTemplate, language = 'nl' }) => {
  
  // Text translations
  const text = {
    nl: {
      title: 'Gecodeerde links',
      close: 'Sluiten',
      copy: 'Kopieer naar klembord'
    },
    en: {
      title: 'Encoded links',
      close: 'Close',
      copy: 'Copy to clipboard'
    }
  };

  const t = text[language] || text.nl;
  
  const generateLinks = () => {
    if (!selectedItems || selectedItems.length === 0) {
      return '';
    }

    // Use default template if none provided
    const template = linkTemplate || [
      { type: 'field', value: 'Url', transform: 'baseUrl' },
      { type: 'text', value: '?utm_source=' },
      { type: 'field', value: 'Bureau' },
      { type: 'text', value: '_' },
      { type: 'field', value: 'Titel' },
      { type: 'text', value: '_' },
      { type: 'field', value: 'Doelgroep' },
      { type: 'text', value: '&utm_campaign=' },
      { type: 'field', value: 'Campagne' },
      { type: 'text', value: '_' },
      { type: 'field', value: 'Datum', transform: 'removeSlashes' },
      { type: 'text', value: '&utm_medium=' },
      { type: 'field', value: 'Medium' },
      { type: 'text', value: '&utm_content=' },
      { type: 'field', value: 'Linknaam' },
      { type: 'text', value: '_' },
      { type: 'field', value: 'Uiting' },
      { type: 'field', value: 'Url', transform: 'fragment' }
    ];

    let linkString = '';

    selectedItems.forEach((item) => {
      let link = '';
      let hasQueryParam = false;

      // Check if URL already has query params
      if (item.Url && item.Url.indexOf('?') !== -1) {
        hasQueryParam = true;
      }

      template.forEach((segment) => {
        if (segment.type === 'text') {
          // Replace ? with & if URL already has query params
          let textValue = segment.value;
          if (textValue === '?' && hasQueryParam) {
            textValue = '&';
          } else if (textValue === '?') {
            hasQueryParam = true;
          }
          link += textValue;
        } else if (segment.type === 'field') {
          const fieldValue = item[segment.value] || '';
          
          // Handle special transforms
          if (segment.transform === 'baseUrl') {
            // Extract base URL (before # and ?)
            const url = fieldValue;
            const hashIndex = url.indexOf('#');
            const queryIndex = url.indexOf('?');
            
            let baseUrl = url;
            if (hashIndex !== -1 && queryIndex !== -1) {
              baseUrl = url.substring(0, Math.min(hashIndex, queryIndex));
            } else if (hashIndex !== -1) {
              baseUrl = url.substring(0, hashIndex);
            } else if (queryIndex !== -1) {
              baseUrl = url.substring(0, queryIndex);
              hasQueryParam = true;
            }
            link += baseUrl;
          } else if (segment.transform === 'fragment') {
            // Extract fragment (after #)
            const url = fieldValue;
            const hashIndex = url.indexOf('#');
            if (hashIndex !== -1) {
              link += url.substring(hashIndex);
            }
          } else if (segment.transform === 'removeSlashes') {
            // Remove slashes from date
            link += fieldValue.replace(/\//g, '');
          } else {
            link += fieldValue;
          }
        }
      });

      // Ensure output is ASCII-safe and a valid URL format
      const asciiLink = encodeURI(link);
      linkString += asciiLink + '\n';
    });

    return linkString;
  };

  const handleCopy = () => {
    const textArea = document.getElementById('codedLinks');
    if (textArea) {
      textArea.select();
      document.execCommand('copy');
      // Use modern clipboard API as fallback
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(textArea.value).catch(() => {
          // Fallback handled by execCommand above
        });
      }
      // Silently copy without alert
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${theme.borderRadius.lg}px`,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        pb: 2
      }}>
        {t.title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.colors.textSecondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ paddingTop: '32px !important' }}>
        <Box>
          <StyledTextarea
            id="codedLinks"
            value={generateLinks()}
            readOnly
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 3, borderTop: `1px solid ${theme.colors.border}` }}>
        <Button onClick={onClose} sx={{ color: theme.colors.textSecondary }}>
          {t.close}
        </Button>
        <StyledButton onClick={handleCopy}>
          {t.copy}
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};

export default LinkCoderDialog;
